"use client";

import { useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { formatDateTime } from "@/lib/format-number";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  type CalendarMode,
  dateFromJdn,
  formatTimeOfDay,
  JD_OF_UNIX_EPOCH,
  jdFromParts,
  jdnFromDate,
  MAX_JDN,
  MIN_JDN,
  MJD_OFFSET,
  parseTimeOfDay,
  partsFromJd,
  RJD_OFFSET,
  TJD_OFFSET,
  weekdayFromJdn,
} from "@/lib/tool-kit/calendars/julian-day";
import type { CivilDate } from "@/lib/tool-kit/converters/week-number";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** English strings. A translated content file overrides these through `ui`. */
export const JULIAN_DAY_CALCULATOR_STRINGS = {
  inputKind: "Start from",
  kindDate: "Calendar date and time",
  kindJd: "Julian Day (JD)",
  kindMjd: "Modified Julian Day (MJD)",
  year: "Year (astronomical, 0 = 1 BCE)",
  month: "Month",
  day: "Day",
  time: "Time of day (UTC, 24-hour)",
  timePlaceholder: "12:00:00",
  timeHint: "HH:MM or HH:MM:SS. Leave blank for midnight.",
  calendar: "Calendar",
  calGregorian: "Gregorian throughout (proleptic)",
  calJulian: "Julian throughout (proleptic)",
  calHistorical: "Historical: Julian before 15 October 1582",
  jdLabel: "Julian Day",
  mjdLabel: "Modified Julian Day",
  jdnLabel: "Julian Day Number",
  jdnHint: "Whole number of the day that holds this noon",
  unixLabel: "Unix time (seconds)",
  weekdayLabel: "Day of the week",
  dateHeading: "The date",
  gregorianLine: "Gregorian: {date}",
  julianLine: "Julian: {date}",
  historicalLine: "Historical calendar: {date}",
  dateFormat: "{day} {month} {year}",
  bce: "{year} BCE",
  ce: "{year}",
  otherHeading: "Other day counts",
  nameColumn: "Count",
  valueColumn: "Value",
  definitionColumn: "Definition",
  rjd: "Reduced Julian Day",
  rjdDef: "JD − 2 400 000",
  tjd: "Truncated Julian Day (NASA)",
  tjdDef: "JD − 2 440 000.5",
  rd: "Rata Die",
  rdDef: "Days since 1 January 1 CE (Gregorian) = 1",
  pickDate: "Enter a year, month and day.",
  pickJd: "Enter a Julian Day.",
  pickMjd: "Enter a Modified Julian Day.",
  invalidYear: "Enter a whole-number year from −4712 to 9999.",
  invalidDay: "Enter the day of the month as a whole number.",
  invalidTime: "Enter the time as HH:MM or HH:MM:SS, from 00:00 to 23:59:59.",
  noSuchDate:
    "That date does not exist in the chosen calendar. The historical calendar skipped 5–14 October 1582, and 29 February exists only in leap years.",
  outsideRange:
    "That value is outside the range from 1 January 4713 BCE to 31 December 9999.",
  timeScaleNote:
    "A Julian Day is only as exact as the time scale behind it. If your date is in UTC, the JD is on the UTC scale; Terrestrial Time (TT, used for J2000.0) runs ahead of UTC by about 69 seconds today.",
  now: "Use the current time (UTC)",
  copy: "Copy Julian Day",
  copied: "Copied!",
} as const;

type Strings = typeof JULIAN_DAY_CALCULATOR_STRINGS;
type InputKind = "date" | "jd" | "mjd";

interface DateFields {
  year: string;
  month: string;
  day: string;
  time: string;
}

interface Moment {
  jd: number;
  jdn: number;
  secondsOfDay: number;
}

type Reading = { moment: Moment } | { error: string } | null;

function momentFromJd(jd: number): Moment | null {
  const parts = partsFromJd(jd);
  if (parts.jdn < MIN_JDN || parts.jdn > MAX_JDN) {
    return null;
  }
  return { jd, ...parts };
}

function readDate(
  fields: DateFields,
  mode: CalendarMode,
  strings: Strings
): Reading {
  if (fields.year.trim() === "" || fields.day.trim() === "") {
    return null;
  }
  const year = parseIntegerInput(fields.year);
  if (year === null || year < -4712 || year > 9999) {
    return { error: strings.invalidYear };
  }
  const day = parseIntegerInput(fields.day);
  if (day === null) {
    return { error: strings.invalidDay };
  }
  const seconds = fields.time.trim() === "" ? 0 : parseTimeOfDay(fields.time);
  if (seconds === null) {
    return { error: strings.invalidTime };
  }
  const jdn = jdnFromDate(mode, { year, month: Number(fields.month), day });
  if (jdn === null) {
    return { error: strings.noSuchDate };
  }
  return {
    moment: { jd: jdFromParts(jdn, seconds), jdn, secondsOfDay: seconds },
  };
}

function readNumber(text: string, offset: number, strings: Strings): Reading {
  if (text.trim() === "") {
    return null;
  }
  const value = parseNumberInput(text);
  const moment = value === null ? null : momentFromJd(value + offset);
  return moment ? { moment } : { error: strings.outsideRange };
}

function useDateFormat(strings: Strings) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (date: CivilDate) =>
    t(strings.dateFormat, {
      day: String(date.day),
      month: formatDateTime(
        locale,
        new Date(Date.UTC(2001, date.month - 1, 1)),
        {
          month: "long",
          timeZone: "UTC",
        }
      ),
      year:
        date.year > 0
          ? t(strings.ce, { year: String(date.year) })
          : t(strings.bce, {
              year: fmt(1 - date.year, { useGrouping: false }),
            }),
    });
}

function ResultPanel({
  moment,
  mode,
  strings,
}: {
  moment: Moment;
  mode: CalendarMode;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const formatDate = useDateFormat(strings);
  const weekday = formatDateTime(
    locale,
    new Date(Date.UTC(2001, 0, 7 + weekdayFromJdn(moment.jdn))),
    { weekday: "long", timeZone: "UTC" }
  );
  const decimals = (value: number, digits: number) =>
    fmt(value, {
      useGrouping: false,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  const { jd } = moment;
  const time = formatTimeOfDay(moment.secondsOfDay);
  const dateLines = [
    t(strings.gregorianLine, {
      date: `${formatDate(dateFromJdn("gregorian", moment.jdn))} ${time}`,
    }),
    t(strings.julianLine, {
      date: `${formatDate(dateFromJdn("julian", moment.jdn))} ${time}`,
    }),
  ];
  if (mode === "historical") {
    dateLines.push(
      t(strings.historicalLine, {
        date: `${formatDate(dateFromJdn("historical", moment.jdn))} ${time}`,
      })
    );
  }
  const unix = Math.round((jd - JD_OF_UNIX_EPOCH) * 86_400 * 1000) / 1000;
  const rows = [
    [strings.rjd, decimals(jd - RJD_OFFSET, 6), strings.rjdDef],
    [strings.tjd, decimals(jd - TJD_OFFSET, 6), strings.tjdDef],
    [strings.rd, decimals(jd - 1_721_424.5, 6), strings.rdDef],
  ];
  return (
    <>
      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>
          {strings.jdLabel}: {decimals(jd, 6)}
        </h2>
        <StatGrid>
          <StatCard emphasis label={strings.jdLabel} value={decimals(jd, 6)} />
          <StatCard
            label={strings.mjdLabel}
            value={decimals(jd - MJD_OFFSET, 6)}
          />
          <StatCard
            label={strings.jdnLabel}
            value={fmt(moment.jdn, { useGrouping: false })}
          />
          <StatCard
            label={strings.unixLabel}
            value={fmt(unix, { useGrouping: false, maximumFractionDigits: 3 })}
          />
          <StatCard label={strings.weekdayLabel} value={weekday} />
        </StatGrid>
        <ul className="space-y-1 text-foreground text-sm leading-relaxed">
          {dateLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          text={jd.toFixed(6)}
        />
      </section>
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.otherHeading}</h2>
        <DataTable
          head={[
            strings.nameColumn,
            strings.valueColumn,
            strings.definitionColumn,
          ]}
          rows={rows}
        />
      </section>
      <Notice>{strings.timeScaleNote}</Notice>
    </>
  );
}

function nowFields(): DateFields {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    year: String(now.getUTCFullYear()),
    month: String(now.getUTCMonth() + 1),
    day: String(now.getUTCDate()),
    time: `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}`,
  };
}

function DateInputs({
  fields,
  setField,
  strings,
}: {
  fields: DateFields;
  setField: (key: keyof DateFields) => (value: string) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  return (
    <div className="grid gap-4 sm:grid-cols-4">
      <TextField
        id="jd-year"
        label={strings.year}
        numeric
        onChange={setField("year")}
        value={fields.year}
      />
      <SelectField
        id="jd-month"
        label={strings.month}
        onChange={setField("month")}
        options={MONTHS.map((month) => ({
          value: String(month),
          label: formatDateTime(
            locale,
            new Date(Date.UTC(2001, month - 1, 1)),
            {
              month: "long",
              timeZone: "UTC",
            }
          ),
        }))}
        value={fields.month}
      />
      <TextField
        id="jd-day"
        label={strings.day}
        numeric
        onChange={setField("day")}
        value={fields.day}
      />
      <TextField
        hint={strings.timeHint}
        id="jd-time"
        label={strings.time}
        onChange={setField("time")}
        placeholder={strings.timePlaceholder}
        value={fields.time}
      />
    </div>
  );
}

export default function JulianDayCalculatorWidget() {
  const strings = useToolUi(JULIAN_DAY_CALCULATOR_STRINGS);
  const [kind, setKind] = useState<InputKind>("date");
  const [mode, setMode] = useState<CalendarMode>("historical");
  const [fields, setFields] = useState<DateFields>({
    year: "",
    month: "1",
    day: "",
    time: "",
  });
  const [jdText, setJdText] = useState("2451545");
  const [mjdText, setMjdText] = useState("51544.5");

  useEffect(() => {
    setFields((current) => (current.year === "" ? nowFields() : current));
  }, []);

  const setField = (key: keyof DateFields) => (value: string) =>
    setFields((current) => ({ ...current, [key]: value }));
  let reading: Reading;
  if (kind === "date") {
    reading = readDate(fields, mode, strings);
  } else if (kind === "jd") {
    reading = readNumber(jdText, 0, strings);
  } else {
    reading = readNumber(mjdText, MJD_OFFSET, strings);
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.inputKind}
        name="jd-kind"
        onChange={setKind}
        options={[
          { value: "date", label: strings.kindDate },
          { value: "jd", label: strings.kindJd },
          { value: "mjd", label: strings.kindMjd },
        ]}
        value={kind}
      />
      {kind === "date" ? (
        <div className="space-y-4">
          <DateInputs fields={fields} setField={setField} strings={strings} />
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[260px] flex-1">
              <SelectField
                id="jd-calendar"
                label={strings.calendar}
                onChange={setMode}
                options={[
                  { value: "historical", label: strings.calHistorical },
                  { value: "gregorian", label: strings.calGregorian },
                  { value: "julian", label: strings.calJulian },
                ]}
                value={mode}
              />
            </div>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setFields(nowFields())}
              type="button"
            >
              {strings.now}
            </button>
          </div>
        </div>
      ) : null}
      {kind === "jd" ? (
        <TextField
          id="jd-value"
          label={strings.jdLabel}
          numeric
          onChange={setJdText}
          value={jdText}
        />
      ) : null}
      {kind === "mjd" ? (
        <TextField
          id="jd-mjd"
          label={strings.mjdLabel}
          numeric
          onChange={setMjdText}
          value={mjdText}
        />
      ) : null}
      {reading === null ? (
        <Notice>
          {
            {
              date: strings.pickDate,
              jd: strings.pickJd,
              mjd: strings.pickMjd,
            }[kind]
          }
        </Notice>
      ) : null}
      {reading !== null && "error" in reading ? (
        <ErrorNotice>{reading.error}</ErrorNotice>
      ) : null}
      {reading !== null && "moment" in reading ? (
        <ResultPanel
          mode={kind === "date" ? mode : "historical"}
          moment={reading.moment}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
